import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Notice, type NoticeTone } from '@/components/ui/notice';
import {
  billingKeys,
  useBillingSummary,
  useInvoices,
  useOpenPortal,
  usePaymentMethod,
  useStartSubscription,
  useStartTopup,
} from '@/hooks/useBillingQueries';
import { formatAud } from '@/utils/money-utils';
import {
  SettingsPage,
  SettingsRow,
  SettingsRowList,
  SettingsSection,
  SettingsStateLine,
} from './SettingsPage';

const TOPUP_PRESETS = [5, 10, 25, 50];
const BILLING_DESCRIPTION = 'Manage your subscription, wallet credit, and payment method.';

function humanize(value: string): string {
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

type StatusMessage = { tone: NoticeTone; text: string };

export function BillingSection() {
  const { data, isLoading, isError } = useBillingSummary();
  const startSubscription = useStartSubscription();
  const openPortal = useOpenPortal();
  const startTopup = useStartTopup();
  const paymentMethod = usePaymentMethod();
  const invoices = useInvoices();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(null);

  useEffect(() => {
    const status = searchParams.get('status');
    if (status !== 'success' && status !== 'granted' && status !== 'cancel') return;
    if (status === 'success') {
      setStatusMessage({ tone: 'success', text: 'Payment successful — your balance is up to date.' });
      queryClient.invalidateQueries({ queryKey: billingKeys.summary() });
    } else if (status === 'granted') {
      setStatusMessage({ tone: 'success', text: 'Credit added — no payment was taken.' });
      queryClient.invalidateQueries({ queryKey: billingKeys.summary() });
    } else {
      setStatusMessage({ tone: 'info', text: 'Checkout cancelled.' });
    }
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, queryClient]);

  if (isLoading) {
    return (
      <SettingsPage title="Billing" description={BILLING_DESCRIPTION}>
        <SettingsStateLine>Loading billing…</SettingsStateLine>
      </SettingsPage>
    );
  }
  if (isError || !data) {
    return (
      <SettingsPage title="Billing" description={BILLING_DESCRIPTION}>
        <SettingsStateLine tone="destructive">Couldn't load billing. Refresh to try again.</SettingsStateLine>
      </SettingsPage>
    );
  }

  const { summary, entries } = data;
  const subscriptionActive =
    summary.subscriptionStatus === 'ACTIVE' || summary.subscriptionStatus === 'TRIALING';
  const invoiceList = invoices.data?.invoices ?? [];
  const card = paymentMethod.data?.paymentMethod;

  return (
    <SettingsPage title="Billing" description={BILLING_DESCRIPTION}>
      {statusMessage && (
        <div className="px-4 pt-3">
          <Notice tone={statusMessage.tone}>{statusMessage.text}</Notice>
        </div>
      )}

      <SettingsSection title="Subscription">
        <SettingsRowList>
          <SettingsRow
            title={<span>{summary.subscriptionStatus ? humanize(summary.subscriptionStatus) : 'No subscription'}</span>}
            detail={
              subscriptionActive && summary.currentPeriodEnd
                ? `${summary.cancelAtPeriodEnd ? 'Cancels on ' : 'Renews on '}${new Date(summary.currentPeriodEnd).toLocaleDateString()}`
                : undefined
            }
            trailing={
              subscriptionActive ? (
                <Button variant="outline" size="sm" onClick={() => openPortal.mutate()} disabled={openPortal.isPending}>
                  Manage subscription
                </Button>
              ) : (
                <Button size="sm" onClick={() => startSubscription.mutate()} disabled={startSubscription.isPending}>
                  Subscribe
                </Button>
              )
            }
          />
        </SettingsRowList>
      </SettingsSection>

      <SettingsSection title="Wallet balance">
        <SettingsRowList>
          <SettingsRow
            title={<span>{formatAud(summary.balanceAud)}</span>}
            meta={summary.lowBalance ? <Badge variant="warning">Low balance</Badge> : null}
            detail="Credit used by Neo and background processes."
            trailing={TOPUP_PRESETS.map((amount) => (
              <Button
                key={amount}
                variant="outline"
                size="sm"
                onClick={() => startTopup.mutate(amount)}
                disabled={startTopup.isPending}
              >
                Add ${amount}
              </Button>
            ))}
          />
        </SettingsRowList>
      </SettingsSection>

      <SettingsSection title="Recent activity">
        {entries.length === 0 ? (
          <SettingsStateLine>No wallet activity yet.</SettingsStateLine>
        ) : (
          <SettingsRowList>
            {entries.map((entry) => (
              <SettingsRow
                key={entry.id}
                title={humanize(entry.kind)}
                detail={`${new Date(entry.createdAt).toLocaleDateString()}${entry.note ? ` · ${entry.note}` : ''}`}
                trailing={
                  <div className="flex flex-col items-end text-sm">
                    <span>{formatAud(entry.amountAud)}</span>
                    <span className="text-xs text-muted-foreground">Balance {formatAud(entry.balanceAfterAud)}</span>
                  </div>
                }
              />
            ))}
          </SettingsRowList>
        )}
      </SettingsSection>

      <SettingsSection title="Payment">
        <SettingsRowList>
          <SettingsRow
            title={
              card
                ? `${humanize(card.brand)} •••• ${card.last4}`
                : 'No payment method on file'
            }
            detail={card ? `Expires ${String(card.expMonth).padStart(2, '0')}/${card.expYear}` : undefined}
            trailing={
              <Button variant="outline" size="sm" onClick={() => openPortal.mutate()} disabled={openPortal.isPending}>
                Update
              </Button>
            }
          />
        </SettingsRowList>
      </SettingsSection>

      <SettingsSection title="Invoices">
        {invoiceList.length === 0 ? (
          <SettingsStateLine>No invoices yet.</SettingsStateLine>
        ) : (
          <SettingsRowList>
            {invoiceList.map((inv) => {
              const href = inv.hostedInvoiceUrl ?? inv.invoicePdf;
              return (
                <SettingsRow
                  key={inv.id}
                  title={new Date(inv.createdIso).toLocaleDateString()}
                  detail={humanize(inv.status)}
                  trailing={
                    <>
                      <span className="text-sm">
                        {inv.amountDisplay} {inv.currency.toUpperCase()}
                      </span>
                      {href ? (
                        <Button variant="ghost" size="sm" asChild>
                          <a href={href} target="_blank" rel="noopener noreferrer">
                            View
                          </a>
                        </Button>
                      ) : null}
                    </>
                  }
                />
              );
            })}
          </SettingsRowList>
        )}
      </SettingsSection>
    </SettingsPage>
  );
}
